import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';
import { NEARBY_DEFAULT_RADIUS } from '@/features/billboards/utils/nearbyBillboardsUtils';

export type BillboardListParams = {
  page?: number;
  limit?: number;
  city?: string;
  search?: string;
  locality?: string;
  type?: 'TRENDING' | 'MOST_VIEWED' | 'NEWLY_LAUNCHED' | string;
  screenOrientation?: string;
  screenType?: string;
  screenOwner?: string;
  sortBy?: string;
  sortByAmount?: 'asc' | 'desc';
  locationType?: string | string[];
  venueType?: string | string[];
  VenueType?: string | string[];
  VenueSubType?: string | string[];
  assignStatus?: string;
  isTesting?: boolean;
  areaFilter?: boolean;
  /** Stored address.area text (e.g. Kolar Road). Filtered on the client. */
  area?: string;
  lat?: number | null;
  lng?: number | null;
  radius?: number;
  startDate?: string;
  endDate?: string;
};

export type VenueHierarchyFilters = {
  locationType?: string;
  venueType?: string;
  VenueSubType?: string;
  city?: string;
  page?: number;
  limit?: number;
};

function isEmptyParam(value: unknown) {
  if (value === '' || value === null || value === undefined) return true;
  return Array.isArray(value) && value.length === 0;
}

function cleanParams<T extends Record<string, unknown>>(params: T) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => !isEmptyParam(value)),
  );
}

const SCREEN_TECH_TYPES = new Set(['LED', 'LCD', 'OLED', 'DIGITAL', 'STATIC']);

function isScreenTechType(value: string) {
  return SCREEN_TECH_TYPES.has(value.trim().toUpperCase());
}

export async function fetchAllBillboards(params: BillboardListParams = {}) {
  const filtered = cleanParams({
    ...params,
    city:
      typeof params.city === 'string' ? params.city.trim() : params.city,
  }) as Record<string, unknown>;

  // Legacy URLs may pass hierarchy filter as screenType (e.g. beauty-grooming)
  if (
    filtered.screenType &&
    !filtered.venueType &&
    !filtered.VenueType &&
    typeof filtered.screenType === 'string' &&
    !isScreenTechType(filtered.screenType)
  ) {
    filtered.venueType = filtered.screenType;
    delete filtered.screenType;
  }

  if (filtered.VenueType && !filtered.venueType) {
    filtered.venueType = filtered.VenueType;
  }
  delete filtered.VenueType;

  if (filtered.lat && filtered.lng) {
    filtered.radius = filtered.radius || NEARBY_DEFAULT_RADIUS;
    filtered.areaFilter = filtered.areaFilter ?? true;
  } else {
    delete filtered.radius;
    delete filtered.areaFilter;
  }

  // Place search is resolved to coordinates — never send locality text to the API.
  delete filtered.locality;
  // Area name is matched on the client; the list API does not filter address.area.
  delete filtered.area;

  // FE sort keys → API sortByAmount where applicable
  if (filtered.sortBy === 'PRICE_ASC') {
    filtered.sortByAmount = 'asc';
    delete filtered.sortBy;
  } else if (filtered.sortBy === 'PRICE_DESC') {
    filtered.sortByAmount = 'desc';
    delete filtered.sortBy;
  } else if (filtered.sortBy) {
    // NAME_* / NEWEST are not supported by this endpoint yet — don't send invalid values
    delete filtered.sortBy;
  }

  const { data } = await httpClient.get(endpoints.billboards.list, {
    params: filtered,
    paramsSerializer: {indexes: null}
    // paramsSerializer: serializeListParams,
  });
  return data;
}

const CATALOG_PAGE_SIZE = 100;
const CATALOG_MAX_PAGES = 50;

export async function fetchBillboardCatalog(params: BillboardListParams = {}) {
  const baseParams: BillboardListParams = {
    city: params.city,
    type: params.type,
    search: params.search,
    limit: CATALOG_PAGE_SIZE,
  };
  const first = await fetchAllBillboards({
    ...baseParams,
    page: 1,
  });
  const firstList = Array.isArray(first?.data?.billboards)
    ? first.data.billboards
    : [];
  const reportedTotal = Number(first?.data?.pagination?.total) || firstList.length;
  const pageSize =
    Number(first?.data?.pagination?.limit) || CATALOG_PAGE_SIZE;
  const pages = Math.min(
    Math.max(
      Number(first?.data?.pagination?.pages) || 0,
      Number(first?.data?.pagination?.totalPages) || 0,
      Math.ceil(reportedTotal / pageSize) || 1,
      1,
    ),
    CATALOG_MAX_PAGES,
  );
  const billboards = [...firstList];

  for (let page = 2; page <= pages; page++) {
    const next = await fetchAllBillboards({
      ...baseParams,
      page,
    });
    if (Array.isArray(next?.data?.billboards)) {
      billboards.push(...next.data.billboards);
    }
  }

  return { billboards, reportedTotal };
}





export async function fetchDynamicVenues(
  filters: VenueHierarchyFilters | string = {},
  selectedCity?: string,
) {
  const normalized =
    typeof filters === 'string' ? { locationType: filters } : { ...filters };

  // hierarchy-v2 returns the full tree; city is optional context only.
  const params = cleanParams({
    city: normalized.city || selectedCity,
  });

  const { data } = await httpClient.get(endpoints.billboards.hierarchy, {
    params,
  });
  return data;
}

export type NearbyBillboardsParams = {
  lat?: number | null;
  lng?: number | null;
  radius?: number;
  city?: string;
};

export type NearbyBillboardsResponse = {
  data?: { billboards?: NearestBillboard[] } | NearestBillboard[];
  billboards?: NearestBillboard[];
  message?: string;
};

export async function fetchNearbyBillboards(params: NearbyBillboardsParams = {}) {
  const filtered = cleanParams({
    radius: params.radius ?? NEARBY_DEFAULT_RADIUS,
    lat: params.lat,
    lng: params.lng,
    city: params.city,
  });

  const { data } = await httpClient.get(endpoints.locations.nearbyBillboards, {
    params: filtered,
  });
  return data as NearbyBillboardsResponse;
}

export async function fetchBillboardDetail(billboardId: string) {
  const { data } = await httpClient.get(endpoints.billboards.detail, {
    params: { billboardId },
  });
  return data as { data?: BillboardDetailData; message?: string };
}

export type BillboardMedia = {
  id?: string;

  url?: string;
  mediaType?: string;
};

export type NearestBillboard = {
  _id?: string;
  id?: string;
  name?: string;

  screenName?: string;
  thumbnail?: { url?: string };
  address?: { city?: string; building?: string };
  city?: string;
  basePrice?: number;
  latitude?: number | string;
  longitude?: number | string;
  lattitude?: number | string;
};

export type BillboardGuideline = {
  _id?: string;
  id?: string;
  name?: string;


  title?: string;
  description?: string;
  status?: string;
  isAdminApproved?: boolean;
};

export type BillboardDetailData = {
  _id?: string;
  id?: string;
  name?: string;


  screenId?: string;
  screenName?: string;
  screenOrientation?: string;
  aboutScreen?: string;
  specification?: string;
  guidelines?: string;
  basePrice?: number;
  timeSlotType?: string;
  locationType?: string;
  screenType?: string;
  VenueType?: string;
  VenueSubType?: string;
  latitude?: number | string;
  longitude?: number | string;
  lattitude?: number | string;
  totalViews?: number;
  avgViews?: number;
  femaleViews?: number;
  maleViews?: number;
  thumbnail?: { url?: string };
  mediaIds?: BillboardMedia[];
  deviceId?: {
    deviceStatus?: string;
    deviceName?: string;
  };
  address?: {
    building?: string;
    area?: string;
    locality?: string;
    secondaryAddress?: string;
    addressLine1?: string;
    fullAddress?: string;
    city?: string;
    district?: string;
    state?: string;
    pincode?: string;
    country?: string;
  };
  pincode?: string;
  nearestBillboards?: NearestBillboard[];
  governmentGuidelines?: BillboardGuideline[];
  screenGuideline?: BillboardGuideline[];
};
